/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Anchor, Badge, Card, Form as NXUIForm, Lightbox } from '@nexigendigital/nxui'
import { TwoFactorType } from 'models/authentication'

/**********************************************************************************************************
 *   API QUERIES
 **********************************************************************************************************/
import { useRemoveTwoFactorMutation, useTwoFactorQuery, useUpdatePrimaryTwoFactorMutation } from 'api/account'

/**********************************************************************************************************
 *   LIGHTBOXES
 **********************************************************************************************************/
import ConfigureAppAuthLightbox from 'containers/account/lightbox/configureAppAuthLightbox'
import ConfigureEmailAuthLightbox from 'containers/account/lightbox/configureEmailAuthLightbox'
import ConfigureMobileAuthLightbox from 'containers/account/lightbox/configureMobileAuthLightbox'

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const StyledTwoFactorCard = styled(Card)`
    width: 100%;
    margin-bottom: 0;
    border-radius: 0 0 16px 16px;

    header {
        border-radius: 0;
    }
`

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function TwoFactor() {
    const [lightbox, setLightbox] = useState({
        status: false,
        template: '',
        meta: {
            method: '',
            id: 0
        }
    })
    const { data: twoFactorData, isFetching: twoFactorLoading, isSuccess: twoFactorSuccess } = useTwoFactorQuery()
    const [updatePrimaryTwoFactor] = useUpdatePrimaryTwoFactorMutation()
    const [removeTwoFactor, {
        isLoading: removeTwoFactorLoading,
        isSuccess: removeTwoFactorSuccess
    }] = useRemoveTwoFactorMutation()
    const twoFactorTable = useMemo(() => {
        const newTwoFactorArray = [
            {
                method: 'Authenticator App',
                status: <Badge color={'attention'}>Unconfigured</Badge>,
                primary: <NXUIForm.Switch checked={false} onClick={() => configureMethod('authenticator')} />,
                action: <Anchor color={'primary'} onClick={() => configureMethod('authenticator')}>Configure</Anchor>
            }, {
                method: 'Email Verification',
                status: <Badge color={'attention'}>Unconfigured</Badge>,
                primary: <NXUIForm.Switch checked={false} onClick={() => configureMethod('email')} />,
                action: <Anchor color={'primary'} onClick={() => configureMethod('email')}>Configure</Anchor>
            }, {
                method: 'Mobile Verification',
                status: <Badge color={'attention'}>Unconfigured</Badge>,
                primary: <NXUIForm.Switch checked={false} onClick={() => configureMethod('sms')} />,
                action: <Anchor color={'primary'} onClick={() => configureMethod('sms')}>Configure</Anchor>
            }
        ]

        if (twoFactorSuccess && Array.isArray(twoFactorData) && twoFactorData.length >= 0) {
            twoFactorData.forEach(twoFactor => {
                switch (twoFactor.type) {
                    case 'authenticator':
                        newTwoFactorArray[0] = {
                            method: 'Authenticator App',
                            status: <Badge>Configured</Badge>,
                            primary: <NXUIForm.Switch checked={twoFactor.is_primary} onClick={() => !twoFactor.is_primary ? makePrimary(twoFactor.id) : false} />,
                            action: <Anchor color={'error'} onClick={() => removeMethodLightbox('authenticator', twoFactor.id)}>Remove</Anchor>
                        }
                        break

                    case 'email':
                        newTwoFactorArray[1] = {
                            method: 'Email Verification',
                            status: <Badge>Configured</Badge>,
                            primary: <NXUIForm.Switch checked={twoFactor.is_primary} onClick={() => !twoFactor.is_primary ? makePrimary(twoFactor.id) : false} />,
                            action: <Anchor color={'error'} onClick={() => removeMethodLightbox('email', twoFactor.id)}>Remove</Anchor>
                        }
                        break

                    case 'sms':
                    default:
                        newTwoFactorArray[2] = {
                            method: 'Mobile Verification',
                            status: <Badge>Configured</Badge>,
                            primary: <NXUIForm.Switch checked={twoFactor.is_primary} onClick={() => !twoFactor.is_primary ? makePrimary(twoFactor.id) : false} />,
                            action: <Anchor color={'error'} onClick={() => removeMethodLightbox('sms', twoFactor.id)}>Remove</Anchor>
                        }
                        break
                }
            })
        }

        return newTwoFactorArray
    }, [twoFactorSuccess, twoFactorData])

    /**********************************************************************************************************
     *   FUNCTIONS
     **********************************************************************************************************/
    function closeLightbox() {
        setLightbox({ status: false, template: '', meta: { method: '', id: 0 } })
    }

    function configureMethod(method: TwoFactorType) {
        setLightbox({ status: true, template: 'configure', meta: { ...lightbox.meta, method: method } })
    }

    function makePrimary(id: number) {
        updatePrimaryTwoFactor(id)
    }

    function removeMethodLightbox(twoFactor: TwoFactorType, id: number) {
        let method
        switch (twoFactor) {
            case 'sms':
                method = 'Mobile Verification'
                break
            case 'email':
                method = 'Email Verification'
                break
            case 'authenticator':
            default:
                method = 'Authenticator App'
                break
        }
        setLightbox({ status: true, template: 'remove', meta: { method, id } })
    }

    /**********************************************************************************************************
     *   SIDE EFFECTS
     **********************************************************************************************************/
    useEffect(() => {
        if (removeTwoFactorSuccess) closeLightbox()
    }, [removeTwoFactorSuccess])

    /*   RENDER COMPONENT
    **********************************************************************************************************/
    return (
        <>
            <StyledTwoFactorCard
                table={{
                    conditions: {
                        sort: false,
                        loading: (twoFactorLoading || removeTwoFactorLoading) && 'Loading Two Factor Methods...'
                    },
                    render: {
                        columns: () => [
                            {
                                Header: '2FA Method',
                                accessor: 'method'
                            }, {
                                Header: 'Status',
                                accessor: 'status'
                            }, {
                                Header: 'Set as Primary',
                                accessor: 'primary'
                            }, {
                                Header: '',
                                accessor: 'action'
                            }
                        ],
                        data: () => twoFactorTable
                    }
                }}
            />
            <Lightbox
                conditions={{ state: lightbox.status && lightbox.template === 'remove', action: () => closeLightbox() }}
                type={'confirm'}
                icon={'info'}
                title={'Remove Two Factor Method'}
                description={`Are you sure you'd like to remove your ${lightbox.meta.method} as a configured method?`}
                actions={[
                    {
                        label: 'Back',
                        color: 'secondary',
                        disabled: removeTwoFactorLoading,
                        func: closeLightbox
                    },
                    {
                        label: 'Remove',
                        color: 'error',
                        loading: removeTwoFactorLoading,
                        func: () => removeTwoFactor(lightbox.meta.id)
                    }
                ]}
            />
            <ConfigureAppAuthLightbox
                status={lightbox.status && lightbox.template === 'configure' && lightbox.meta.method === 'authenticator'}
                close={() => closeLightbox()} />
            <ConfigureEmailAuthLightbox
                status={lightbox.status && lightbox.template === 'configure' && lightbox.meta.method === 'email'}
                close={() => closeLightbox()} />
            <ConfigureMobileAuthLightbox
                status={lightbox.status && lightbox.template === 'configure' && lightbox.meta.method === 'sms'}
                close={() => closeLightbox()} />
        </>
    )
}
